import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Drawer, Form, Spin, Typography } from 'antd';
import type { TableColumnsType } from 'antd';
import {
  EditOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  PlusOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import { isMarketing } from '../modules/marketing/marketingAccessPolicy';
import SupplierForm from '../components/suppliers/SupplierForm';
import SupplierDefaultCostsTab from '../components/suppliers/SupplierDefaultCostsTab';
import { applySupplierSaveError } from '../components/suppliers/supplierFormErrors';
import { canManageSupplierDefaultCosts, canManageSuppliers } from '../modules/suppliers/supplierAccessPolicy';
import { normalizeSupplierContacts, type SupplierContact } from '../utils/supplierContacts';
import type { Supplier, SupplierSubmitValues } from '../modules/suppliers/types';
import { useFeedback } from '../utils/feedback';
import { useBackNavigation } from '../hooks/useBackNavigation';
import {
  ObjectCardEmpty,
  ObjectCardFacts,
  ObjectCardHero,
  ObjectCardSection,
  type ObjectCardFact,
  ObjectCardTable
} from '../components/layout/ObjectCard';

const { Text } = Typography;

const EMPTY_VALUE = '—';

const formatDateTime = (value?: string) => (value ? new Date(value).toLocaleString('pl-PL') : EMPTY_VALUE);

type ContactRow = SupplierContact & { id: string };

/**
 * Supplier card on the object-card primitives (components/layout/ObjectCard):
 * hero with monogram, contact line and facts strip; contacts and default costs
 * in the main column, details aside. No tabs; editing opens in a side drawer.
 */
const SupplierDetail: React.FC = () => {
  const { t } = useTranslation(['suppliers', 'common']);
  const feedback = useFeedback();
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const { id } = useParams<{ id: string }>();
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const currentUser = authService.getCurrentUser();
  const canEditSuppliers = canManageSuppliers(currentUser);
  const canViewSupplierDefaultCosts = canManageSupplierDefaultCosts(currentUser);
  const canViewOrders = !isMarketing(currentUser);

  const fetchSupplier = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await api.get(`/suppliers/${id}`);
      setSupplier(response.data);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        feedback.warning(t('suppliers:detail.notFound'));
        navigate('/app/suppliers', { replace: true });
        return;
      }
      feedback.error(t('suppliers:messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupplier();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleUpdate = async (values: SupplierSubmitValues) => {
    if (!id) return;
    setSubmitting(true);
    form.setFields([
      { name: 'name', errors: [] },
      { name: 'contactPeople', errors: [] },
      { name: 'taxId', errors: [] }
    ]);

    try {
      await api.put(`/suppliers/${id}`, values);
      feedback.success(t('suppliers:messages.updateSuccess', { name: values.name }));
      setEditing(false);
      await fetchSupplier();
    } catch (error) {
      applySupplierSaveError(error, form, t, feedback, t('suppliers:messages.saveFailed', { name: values.name }));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="obj-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!supplier) {
    return null;
  }

  const renderValue = (value?: string | null) =>
    value ? value : <Text type="secondary">{EMPTY_VALUE}</Text>;

  const contacts: ContactRow[] = normalizeSupplierContacts(
    supplier.contactPeople,
    supplier.contactPerson,
    supplier.phone,
    supplier.email
  ).map((contact, index) => ({ ...contact, id: `${supplier.id}-contact-${index}` }));
  const primaryContact = contacts[0];

  const contactColumns: TableColumnsType<ContactRow> = [
    {
      title: t('suppliers:form.contactName'),
      dataIndex: 'name',
      key: 'name',
      render: (value?: string | null) => <span className="obj-strong">{value || EMPTY_VALUE}</span>
    },
    {
      title: t('suppliers:form.contactPhone'),
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: (value?: string | null) => renderValue(value)
    },
    {
      title: t('suppliers:form.contactEmail'),
      dataIndex: 'email',
      key: 'email',
      render: (value?: string | null) => (value ? <a href={`mailto:${value}`}>{value}</a> : renderValue(value))
    }
  ];

  const facts: ObjectCardFact[] = [
    { key: 'taxId', label: t('suppliers:form.taxId'), icon: <IdcardOutlined />, value: supplier.taxId || t('suppliers:meta.noTaxId'), testId: 'supplier-chip-tax-id' },
    { key: 'currency', label: t('suppliers:form.defaultCurrency'), icon: <WalletOutlined />, value: supplier.defaultCurrency || 'PLN', testId: 'supplier-chip-currency' },
    ...(canViewOrders
      ? [{ key: 'orders', label: t('suppliers:detail.orderCount'), icon: <ShoppingCartOutlined />, value: supplier.orderCount ?? 0, testId: 'supplier-chip-orders' }]
      : []),
    { key: 'contacts', label: t('suppliers:detail.contacts'), icon: <TeamOutlined />, value: contacts.length, testId: 'supplier-chip-contacts' }
  ];

  return (
    <div className="obj-card supplier-card" data-testid="supplier-card">
      <ObjectCardHero
        testId="supplier-card-header"
        kind={t('suppliers:detail.kind')}
        name={supplier.name}
        backLabel={t('suppliers:detail.back')}
        onBack={() => goBack('/app/suppliers')}
        meta={[
          supplier.address && <><EnvironmentOutlined aria-hidden="true" />{supplier.address}</>,
          primaryContact?.name && <><UserOutlined aria-hidden="true" />{primaryContact.name}</>,
          primaryContact?.phone && <><PhoneOutlined aria-hidden="true" />{primaryContact.phone}</>
        ]}
        actions={canEditSuppliers && (
          <Button type="primary" icon={<EditOutlined />} onClick={() => setEditing(true)}>
            {t('common:edit')}
          </Button>
        )}
        facts={facts}
      />

      <div className="obj-card__body">
          <ObjectCardSection
            testId="supplier-section-contacts" tone="green"
            title={t('suppliers:detail.contacts')}
            icon={<TeamOutlined />}
            count={contacts.length}
          >
            {contacts.length > 0 ? (
              <ObjectCardTable<ContactRow>
                size="small"
                columns={contactColumns}
                dataSource={contacts}
                rowKey="id"
                pagination={false}
              />
            ) : (
              <ObjectCardEmpty
                icon={<UserOutlined />}
                text={t('suppliers:detail.noContacts')}
                action={canEditSuppliers && (
                  <Button size="small" icon={<PlusOutlined />} onClick={() => setEditing(true)}>
                    {t('suppliers:form.addContact')}
                  </Button>
                )}
              />
            )}
          </ObjectCardSection>

          <ObjectCardSection testId="supplier-section-basic" tone="gray" title={t('suppliers:detail.basicInfo')} icon={<InfoCircleOutlined />}>
            <ObjectCardFacts
              items={[
                { label: t('suppliers:form.address'), value: renderValue(supplier.address) },
                { label: t('suppliers:detail.createdAt'), value: formatDateTime(supplier.createdAt) },
                { label: t('suppliers:detail.updatedAt'), value: formatDateTime(supplier.updatedAt) }
              ]}
            />
          </ObjectCardSection>

          {/* Default costs are supplier-side costs: the backend gates GET and writes
              with requireSupplierDefaultCostManagement (ADMIN/OPERATIONS only, not
              Handlowiec) — hide the section for other roles instead of rendering
              one that always errors on load. */}
          {canViewSupplierDefaultCosts && (
            <SupplierDefaultCostsTab supplierId={supplier.id} canEdit={canViewSupplierDefaultCosts} />
          )}
      </div>

      <Drawer
        title={t('suppliers:editSupplier')}
        open={editing}
        onClose={() => setEditing(false)}
        width={640}
        rootClassName="obj-drawer"
        maskClosable={false}
        destroyOnClose
      >
        <SupplierForm
          form={form}
          editingSupplier={supplier}
          submitting={submitting}
          submitLabel={t('common:save')}
          onSubmit={handleUpdate}
          open={editing}
          onCancel={() => setEditing(false)}
        />
      </Drawer>
    </div>
  );
};

export default SupplierDetail;
